import {
  Assignment,
  CalendarMonth,
  Description,
  Favorite,
  FileCopy,
  History,
  MedicalServices,
  Medication,
  Watch,
} from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { isIOS } from "react-device-detect";

import { MobileBottomSheet } from "@/components/common/MobileBottomSheet";
import { ScreenMobile } from "@/components/common/ScreenMobile";
import { isReactNativeWebView } from "@/utils/useWebViewBridge";
import { ScheduleForDash } from "../../ScheduleForDash";
import { PatientQuestionnaireTasksSection } from "./PatientQuestionnaireTasksSection";
import { DashMobileSection } from "./DashMobileSection";
import { WearableObservations } from "@/pages/wearableobservations/WearableObservations";
import { PatientSupportingDocumentsDashButton } from "./patientSupportingDocuments/PatientSupportingDocumentsDashButton";

type MobileActionId = "exames" | "procedures" | "certificates";

type PaletteAccent =
  | "primary800"
  | "secondary800"
  | "secondary600"
  | "success700"
  | "error700";

interface MobileAction {
  id:
    | MobileActionId
    | "agendar"
    | "registros"
    | "fluxHealth"
    | "googleHealth"
    | "accessHistory";
  label: string;
  icon: React.ComponentType<SvgIconProps>;
  accent: PaletteAccent;
  /** Atalho direto — sem passar pelo PatientDash. */
  href?: string;
}

/**
 * Atalhos rápidos do dash mobile.
 * Dados Wearable → ScreenMobile (oculto no iOS).
 * FluxHealth → rota /googlehealth.
 * No iOS: Histórico de acessos ocupa o lugar do Wearable (2ª da fila de baixo).
 */
const TOP_ACTIONS: MobileAction[] = [
  {
    id: "agendar",
    label: "Agendamento",
    icon: CalendarMonth,
    accent: "primary800",
    href: "/schedulingsearch",
  },
  {
    id: "procedures",
    label: "Procedimentos",
    icon: MedicalServices,
    accent: "error700",
  },
  {
    id: "certificates",
    label: "Relatórios",
    icon: FileCopy,
    accent: "secondary800",
  },
  {
    id: "registros",
    label: "Registros",
    icon: Assignment,
    accent: "primary800",
    href: "/myrecords",
  },
  {
    id: "fluxHealth",
    label: "Dados Wearable",
    icon: Watch,
    accent: "secondary600",
  },
  {
    id: "googleHealth",
    label: "Google Health",
    icon: Favorite,
    accent: "success700",
    href: "/googlehealth",
  },
];

/** Só iOS — 2ª opção da fila de baixo, antes do FluxHealth. */
const ACCESS_HISTORY_ACTION: MobileAction = {
  id: "accessHistory",
  label: "Histórico de acessos",
  icon: History,
  accent: "secondary800",
  href: "/history",
};

interface PatientDashMobileProps {
  userName?: string;
  refreshTokenGoogleFit?: string;
  openAgendaFull: boolean;
  onOpenAgendaFull: (open: boolean) => void;
  onMobileAction: (id: MobileActionId | "medications") => void;
}

function DocumentQuickChip({
  action,
  onClick,
}: {
  action: MobileAction;
  onClick: () => void;
}) {
  const theme = useTheme();
  const accent =
    (theme.palette as unknown as Record<string, { main?: string }>)[
      action.accent
    ]?.main ?? theme.palette.primary.main;

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: "100%",
        minWidth: 0,
        borderRadius: 2,
        py: 0.5,
        px: 0.25,
        bgcolor: "transparent",
        WebkitTapHighlightColor: "transparent",
        "&:active": { transform: "scale(0.96)", opacity: 0.88 },
      }}
    >
      <Stack alignItems="center" spacing={0.85} sx={{ width: "100%" }}>
        <Box
          sx={{
            // Ícones um pouco maiores (3 por linha)
            width: { xs: 48, sm: 56 },
            height: { xs: 48, sm: 56 },
            borderRadius: 2,
            bgcolor: accent,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <action.icon sx={{ fontSize: { xs: 26, sm: 30 } }} />
        </Box>
        <Typography
          component="span"
          variant="caption"
          sx={{
            display: "block",
            fontWeight: 600,
            fontSize: "clamp(0.58rem, 2vw, 0.78rem)",
            lineHeight: 1.15,
            textAlign: "center",
            width: "100%",
            px: 0.1,
            whiteSpace: "pre-line",
            wordBreak: "normal",
            overflowWrap: "normal",
            hyphens: "none",
            color: "text.primary",
          }}
        >
          {action.label}
        </Typography>
      </Stack>
    </ButtonBase>
  );
}

const agendaSectionSx = { mt: 2 };

export function PatientDashMobile({
  refreshTokenGoogleFit: _refreshTokenGoogleFit = "",
  openAgendaFull,
  onOpenAgendaFull,
  onMobileAction,
}: PatientDashMobileProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  /** Mês compartilhado entre o preview da agenda e o modal Ver tudo. */
  const [agendaMonth, setAgendaMonth] = useState<Dayjs>(() => dayjs());
  /** FluxHealth: ScreenMobile com Dados Wearable (comportamento anterior). */
  const [openFluxHealth, setOpenFluxHealth] = useState(false);

  // iOS: sem Dados Wearable; Histórico de acessos na 2ª posição da fila de baixo
  const quickActions = useMemo(() => {
    if (!isIOS) return TOP_ACTIONS;

    const withoutWearable = TOP_ACTIONS.filter(
      (action) => action.id !== "fluxHealth"
    );
    const fluxIdx = withoutWearable.findIndex(
      (action) => action.id === "googleHealth"
    );
    if (fluxIdx < 0) return withoutWearable;

    return [
      ...withoutWearable.slice(0, fluxIdx),
      ACCESS_HISTORY_ACTION,
      ...withoutWearable.slice(fluxIdx),
    ];
  }, []);
  /**
   * Compensa o padding do layout mobile acima da barra de navegação.
   * No fluxmedportable o safe-area inferior já vem do nativo — não duplicar.
   */
  const mobileNavOffset = isReactNativeWebView()
    ? "-96px"
    : "calc(-96px - env(safe-area-inset-bottom, 0px))";

  const scheduleDashProps = {
    compact: true as const,
    listLayout: "slider" as const,
    hideNewAppointmentButton: true,
    dateFilter: agendaMonth,
    onDateFilterChange: setAgendaMonth,
  };

  return (
    <>
      <Box
        sx={{
          px: 1.5,
          pt: 0.5,
          pb: 0,
          mb: mobileNavOffset,
        }}
      >
        {/* 6 atalhos em grade: 3 por linha (sem scroll horizontal) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: { xs: 1, sm: 1.25 },
            width: "100%",
            mt: 2,
          }}
        >
          {quickActions.map((action) => (
            <Box key={action.id} sx={{ minWidth: 0 }}>
              <DocumentQuickChip
                action={action}
                onClick={() => {
                  // Dados Wearable: ScreenMobile
                  if (action.id === "fluxHealth") {
                    setOpenFluxHealth(true);
                    return;
                  }
                  if (action.href) {
                    navigate(action.href);
                    return;
                  }
                  onMobileAction(action.id as MobileActionId);
                }}
              />
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 2 }}>
          <PatientSupportingDocumentsDashButton />
        </Box>

        <Stack direction="row" spacing={1.25} sx={{ mt: 2.25 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Description />}
            onClick={() => onMobileAction("exames")}
            sx={{
              borderRadius: 99,
              py: 1.15,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.8125rem",
              bgcolor: alpha(theme.palette.grey[500], 0.08),
              borderColor: "primary800.main",
              borderWidth: 1.5,
              color: "text.primary",
              "& .MuiButton-startIcon": { color: "primary800.main" },
            }}
          >
            Exames
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Medication />}
            onClick={() => onMobileAction("medications")}
            sx={{
              borderRadius: 99,
              py: 1.15,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.8125rem",
              bgcolor: alpha(theme.palette.grey[500], 0.08),
              borderColor: "primary800.main",
              borderWidth: 1.5,
              color: "text.primary",
              "& .MuiButton-startIcon": { color: "primary800.main" },
            }}
          >
            Medicamentos
          </Button>
        </Stack>

        {/* Agenda — sempre visível; vazio exibe mensagem no lugar do card */}
        <Box sx={agendaSectionSx}>
          <ScheduleForDash
            {...scheduleDashProps}
            dashboardPreview
            onViewAll={() => onOpenAgendaFull(true)}
          />
        </Box>

        <DashMobileSection
          title="Questionários"
          subtitle="Responda pendências da equipe"
          spacingTop={2}
        >
          <PatientQuestionnaireTasksSection
            variant="tutor"
            compact
            compactTabsOnly
            defaultTasksTab="open"
            fullListTodosOnly
          />
        </DashMobileSection>
      </Box>

      <MobileBottomSheet
        open={openAgendaFull}
        onClose={() => onOpenAgendaFull(false)}
        title="Próximas consultas"
        subtitle="Suas consultas do mês"
      >
        <Box sx={{ py: 1, px: 2 }}>
          <ScheduleForDash
            compact
            listLayout="vertical"
            hideNewAppointmentButton
            useDashboardMonthControls
            dateFilter={agendaMonth}
            onDateFilterChange={setAgendaMonth}
          />
        </Box>
      </MobileBottomSheet>

      {/* FluxHealth: Dados Wearable em ScreenMobile (como antes) */}
      <ScreenMobile
        title="Dados Wearable"
        open={openFluxHealth}
        onClose={() => setOpenFluxHealth(false)}
      >
        <Box
          sx={{
            p: 1.5,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflowX: "hidden",
          }}
        >
          {openFluxHealth ? <WearableObservations embedded /> : null}
        </Box>
      </ScreenMobile>
    </>
  );
}
